// Iter 309 — drill-down under one commission deal: grouped per product, then
// every tracked item (100@domain, 101@domain…). Shared by the partner screen.
import { useEffect, useMemo, useState } from "react";
import { Loader2, ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { NewPill } from "./NewPill";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const qty = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const pct = (r) => (r === null || r === undefined ? "—" : `${Number(r).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`);

export function DealLinesPanel({ dealId, showReportLink = true }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [showItems, setShowItems] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    let alive = true;
    setData(null); setErr("");
    api.get(`/commissions/deals/${encodeURIComponent(dealId)}/lines`)
      .then(({ data: d }) => { if (alive) { setData(d); setShowItems(false); } })
      .catch((e) => alive && setErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not load deal detail"));
    return () => { alive = false; };
  }, [dealId]);

  const lines = useMemo(() => {
    if (!data) return [];
    const t = q.trim().toLowerCase();
    if (!t) return data.lines;
    return data.lines.filter((l) => `${l.product_name} ${l.tracked_item} ${l.site} ${l.invoice_number ?? ""}`.toLowerCase().includes(t));
  }, [data, q]);

  if (err) return <div className="px-4 py-3 text-xs text-rose-600" data-testid="deal-lines-error">{err}</div>;
  if (!data) return <div className="px-4 py-4 text-xs text-slate-400 flex items-center gap-2" data-testid="deal-lines-loading"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading deal detail…</div>;
  if (!data.lines.length) {
    return (
      <div className="px-4 py-3 text-xs text-slate-500" data-testid="deal-lines-empty">
        No line detail for this deal — it was imported from a Stax CSV summary, so only the totals are known.
      </div>
    );
  }
  const t = data.totals;
  return (
    <div className="space-y-3 px-4 py-3" data-testid={`deal-lines-${dealId}`}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
        <span><b className="text-slate-800">{t.items}</b> tracked item{t.items === 1 ? "" : "s"}</span>
        <span>qty <b className="text-slate-800">{qty(t.quantity)}</b></span>
        <span>billed <b className="text-slate-800">{money(t.billed_amount)}</b></span>
        <span>commission <b className="text-emerald-700">{money(t.commission)}</b></span>
        {t.unpaid_items > 0 && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">{t.unpaid_items} on unpaid invoices</span>}
        {t.excluded > 0 && <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">{t.excluded} excluded</span>}
        {data.deal_is_new && <NewPill kind="deal" testId="deal-lines-new-deal" />}
        {!data.deal_is_new && t.new_items > 0 && (
          <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 ring-1 ring-inset ring-blue-200"
                title="Items billed for the first time this month" data-testid="deal-lines-new-count">
            {t.new_items} new this month
          </span>
        )}
        {showReportLink && (
          <Link to={`/reports/commissions_deal_detail?frm=${data.period_label}&to=${data.period_label}&domain=${encodeURIComponent(data.domain || "")}&detail=item`}
                className="ml-auto inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-800" data-testid="deal-lines-open-report">
            Open in Reports Hub <ExternalLink className="h-3 w-3" />
          </Link>
        )}
      </div>

      <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
        <table className="w-full text-xs" data-testid="deal-products-table">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-3 py-1.5 text-left">Product</th>
              <th className="px-3 py-1.5 text-right">Qty</th>
              <th className="px-3 py-1.5 text-right">Items</th>
              <th className="px-3 py-1.5 text-right">Billed</th>
              <th className="px-3 py-1.5 text-right">Rate</th>
              <th className="px-3 py-1.5 text-right">Commission</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.products.map((p) => (
              <tr key={p.product_name} data-testid="deal-product-row">
                <td className="px-3 py-1.5 font-medium text-slate-700">{p.product_name}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{qty(p.quantity)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{p.items}{p.excluded_items ? <span className="text-slate-400"> ({p.excluded_items} excl.)</span> : null}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{money(p.billed_amount)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{p.rate_label}</td>
                <td className="px-3 py-1.5 text-right tabular-nums font-medium text-emerald-700">{money(p.commission)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-2">
        <button type="button" onClick={() => setShowItems((v) => !v)} data-testid="deal-items-toggle"
                className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900">
          {showItems ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          Tracked items ({data.lines.length})
        </button>
        {showItems && (
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter items…"
                 className="ml-auto h-7 w-48 rounded-md border border-slate-200 px-2 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-400"
                 data-testid="deal-items-filter" />
        )}
      </div>
      {showItems && (
        <div className="max-h-80 overflow-auto rounded-md border border-slate-200 bg-white">
          <table className="w-full text-xs" data-testid="deal-items-table">
            <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-1.5 text-left">Product</th>
                <th className="px-3 py-1.5 text-left">Tracked item</th>
                <th className="px-3 py-1.5 text-right">Qty</th>
                <th className="px-3 py-1.5 text-right">Unit</th>
                <th className="px-3 py-1.5 text-right">Billed</th>
                <th className="px-3 py-1.5 text-right">Rate</th>
                <th className="px-3 py-1.5 text-right">Commission</th>
                <th className="px-3 py-1.5 text-left">Invoice</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {lines.map((l) => (
                <tr key={l.line_id} className={l.excluded ? "text-slate-400" : ""} data-testid="deal-item-row">
                  <td className="px-3 py-1.5">{l.product_name}</td>
                  <td className="px-3 py-1.5 font-mono text-[11px]">
                    {l.tracked_item || l.site || <span className="text-slate-400">—</span>}
                    {l.is_prorate && <span className="ml-1.5 text-[10px] italic text-slate-400" title={l.prorate_note || "Prorated charge"}
                                           data-testid={`deal-line-prorated-${l.line_id}`}>prorated</span>}
                    {l.is_new && <NewPill first={l.first_period} className="ml-1.5" testId={`deal-line-new-${l.line_id}`} />}
                    {l.excluded && <span className="ml-1 rounded bg-slate-100 px-1 text-[9px]">excluded{l.exclude_reason ? `: ${l.exclude_reason}` : ""}</span>}
                    {l.overridden && !l.excluded && <span className="ml-1 rounded bg-amber-50 px-1 text-[9px] text-amber-700" title={l.note}>override</span>}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{qty(l.quantity)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{l.unit_price === null || l.unit_price === undefined ? "—" : money(l.unit_price)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{money(l.billed_amount)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{pct(l.rate)}</td>
                  <td className={`px-3 py-1.5 text-right tabular-nums font-medium ${l.excluded ? "" : "text-emerald-700"}`}>{money(l.commission)}</td>
                  <td className="px-3 py-1.5 text-slate-500">
                    {l.invoice_number ? `#${l.invoice_number}` : "—"}
                    {l.invoice_number && (
                      <span className={`ml-1 rounded px-1 text-[9px] font-medium ${l.invoice_paid ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                        {l.invoice_paid ? "paid" : "unpaid"}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {lines.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-4 text-center text-slate-400">No items match “{q}”.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
